import {ref} from 'vue'

// Importujemy typ DataLayerObject
import type {DataLayerObject} from '@gtm-support/vue-gtm'

// Declaration for gtag and global variables
declare global {
    interface Window {
        gtag: (
            command: string,
            targetId?: string,
            config?: string | Record<string, any>,
            callback?: (value: any) => void
        ) => void;
        // Functions and variables for GTM/GA4
        initRedlikeGA4: () => void;
        _ga4ClientId?: string;
        dataLayer?: DataLayerObject[];
    }
}

type ItemType = 'post'

// Global reference to store GA4 client_id
const globalClientId = ref<string | null>(null)

// Conditional debug log function using centralized debug mode management
const debugLog = (message: string, ...args: any[]) => {
    const {isDebugMode} = useDebugMode();
    if (isDebugMode()) {
        console.log(`[GA4Debug] ${message}`, ...args)
    }
}

/**
 * Helper function to push events to GTM dataLayer
 * This allows additional integrations via GTM without code changes
 */
const pushToDataLayer = (eventName: string, eventData: Record<string, any>) => {
    if (!process.client || !window.dataLayer) return;

    debugLog(`Pushing event to dataLayer: ${eventName}`, eventData);

    try {
        // Create a clone of the data to avoid reference issues
        const dataLayerEvent = {
            event: eventName,
            ...eventData
        };

        window.dataLayer.push(dataLayerEvent);
        debugLog('Successfully pushed to dataLayer');
    } catch (error) {
        const {isDebugMode} = useDebugMode();
        if (isDebugMode()) {
            console.error('[GA4Debug] Error pushing to dataLayer:', error);
        }
    }
};

/**
 * Function to initialize GA4 client_id
 * This is now just a wrapper around the function defined in the GTM script
 */
export const initGA4ClientId = () => {
    debugLog('Wrapper initGA4ClientId function called');

    if (!process.client) {
        debugLog('Not running in client environment, exiting');
        return;
    }

    try {
        // Call the function defined in the GTM script
        if (window.initRedlikeGA4 && typeof window.initRedlikeGA4 === 'function') {
            debugLog('Calling window.initRedlikeGA4()');
            window.initRedlikeGA4();
        } else {
            const {isDebugMode} = useDebugMode();
            if (isDebugMode()) {
                console.error('[GA4Debug] window.initRedlikeGA4 is not a function');
            }
        }
    } catch (e) {
        const {isDebugMode} = useDebugMode();
        if (isDebugMode()) {
            console.error('[GA4Debug] Failed to call initRedlikeGA4:', e);
        }
    }
}

// Interface for cart item
interface CartItem {
    item_id: string | number;
    item_name: string;
    item_category: string;
    price: number;
    quantity: number;
    [key: string]: any; // Additional optional fields
}

// Interface for e-commerce data
interface EcommerceData {
    currency: string;
    value: number;
    items: CartItem[];
    [key: string]: any; // Additional optional fields
}

export const useEventLogger = () => {
    // Get current client_id from window._ga4ClientId
    const getClientId = () => {
        // First check window._ga4ClientId that's set by the inline script
        if (process.client && window._ga4ClientId) {
            debugLog('Found client_id in window._ga4ClientId:', window._ga4ClientId);
            return window._ga4ClientId;
        }

        debugLog('No client_id available');
        return null;
    };
    
    // Function to clear previous ecommerce object
    const clearEcommerceData = () => {
        if (process.client && window.dataLayer) {
            window.dataLayer.push({ ecommerce: null });
        }
    };
    
    // Function to send add_to_cart event for a single post
    const sendAddToCartPostEvent = (postId: number | string, postAuthor: string, price: number, currency: string) => {
        // First clear previous ecommerce object
        clearEcommerceData();
        
        // Prepare e-commerce data
        const ecommerceData: EcommerceData = {
            currency: currency?.toUpperCase() || 'USD',
            value: price,
            items: [
                {
                    item_id: `post_${postId}`,
                    item_name: `Post by @${postAuthor}`,
                    item_category: 'post',
                    price: price,
                    quantity: 1
                }
            ]
        };
        
        // Send event to GTM
        pushToDataLayer('add_to_cart', { ecommerce: ecommerceData });
    };
    
    // Function to send add_to_cart event for a subscription
    const sendAddToCartSubscriptionEvent = (authorUsername: string, price: number, currency: string) => {
        // First clear previous ecommerce object
        clearEcommerceData();
        
        // Prepare e-commerce data
        const ecommerceData: EcommerceData = {
            currency: currency?.toUpperCase() || 'USD',
            value: price,
            items: [
                {
                    item_id: `subscription_${authorUsername}`,
                    item_name: `Subscription to @${authorUsername}`,
                    item_category: 'subscription',
                    price: price,
                    quantity: 1
                }
            ]
        };
        
        // Send event to GTM
        pushToDataLayer('add_to_cart', { ecommerce: ecommerceData });
    };

    // Send event to backend with client_id attached
    const sendEvent = async (eventType: string, itemId: number, itemType: ItemType = 'post') => {
        debugLog(`sendEvent called: type=${eventType}, itemId=${itemId}, itemType=${itemType}`);

        const {isLoggedIn, user} = useAuthStore();

        if (!isLoggedIn) {
            debugLog('User not logged in, skipping event');
            return;
        }

        // Get the latest client_id
        const clientId = getClientId();

        // Prepare payload with client_id for backend
        const payload = {
            item_id: itemId,
            item_type: itemType,
            event: eventType,
            ga4_client_id: clientId
        };

        debugLog('Event payload prepared:', payload);

        try {
            const url = useRuntimeConfig().public.EVENT_URL;
            debugLog('Sending event to URL:', url + 'event');

            await $larafetch(url + 'event', {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                },
                body: JSON.stringify(payload),
            });

            debugLog('Event successfully sent to backend');

            // Push to dataLayer for GTM integrations after backend event is sent
            pushToDataLayer(`redlike_${eventType}`, {
                item_id: itemId,
                item_type: itemType,
                user_id: isLoggedIn && user ? user.id : null,
                user_logged_in: isLoggedIn
            });

        } catch (error) {
            const {isDebugMode} = useDebugMode();
            if (isDebugMode()) {
                console.error("[GA4Debug] Error sending event:", error);
            }
        }
    };

    return {
        sendEvent,
        getClientId,
        pushToDataLayer,
        clearEcommerceData,
        sendAddToCartPostEvent,
        sendAddToCartSubscriptionEvent
    };
};
